import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const PERIODS = [
    { value: '1m', label: 'Mese corrente' },
    { value: '30d', label: 'Ultimi 30 giorni' },
    { value: '6m', label: 'Ultimi 6 mesi' },
    { value: '1y', label: 'Ultimo anno' },
    { value: 'ytd', label: 'Anno in corso' },
    { value: 'custom', label: 'Personalizzato' },
];

export function Stats() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('6m');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    useEffect(() => {
        setLoading(true);
        const params = { period };
        if (period === 'custom') {
            if (customFrom) params.from = customFrom;
            if (customTo) params.to = customTo;
        }
        api.getDashboard(params).then((d) => {
            setData(d);
            setLoading(false);
        });
    }, [period, customFrom, customTo]);

    if (loading) return <p className="text-gray-500">Caricamento...</p>;

    const trendData = data.trend.map((t) => ({
        name: t.label,
        Entrate: t.income,
        Uscite: t.expense,
    }));

    const categoryData = data.expenses_by_category.map((c) => ({
        name: c.category?.name || 'Non categorizzato',
        value: c.total,
        color: c.category?.color || '#94a3b8',
    }));

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Statistiche</h1>
                <div className="flex flex-col sm:flex-row gap-3">
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="px-3 py-2 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 outline-none bg-white"
                    >
                        {PERIODS.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                    {period === 'custom' && (
                        <>
                            <input
                                type="date"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                                className="px-3 py-2 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            />
                            <input
                                type="date"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                                className="px-3 py-2 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h2 className="font-semibold text-gray-900 mb-4">Cashflow</h2>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                                <Tooltip formatter={(value) => `€ ${value.toFixed(2)}`} />
                                <Bar dataKey="Entrate" fill="#10b981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Uscite" fill="#ef4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h2 className="font-semibold text-gray-900 mb-4">Spese per categoria</h2>
                    <div className="h-72">
                        {categoryData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={categoryData}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={3}
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `€ ${value.toFixed(2)}`} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-gray-400 text-sm">Nessuna spesa nel periodo selezionato</div>
                        )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3">
                        {categoryData.map((c) => (
                            <div key={c.name} className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                                {c.name}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
